import { useState, useEffect, useEffectEvent } from 'react';
import {
  useNavigate,
  useLocation,
  useSearchParams,
  type Location,
} from 'react-router';
import { useAuth } from '@/hooks/useAuthHook';
import { GoogleLogin } from '@/components/auth/GoogleLogin';
import { MagicLinkForm } from '@/components/auth/MagicLinkForm';
import { DevLoginSection } from '@/components/auth/DevLoginSection';
import { safeReturnPath } from '@/utils/safe-return-path';
import dffLogo from '@/assets/dff-logo-blue.svg';

const LoginPage = () => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [linkSent, setLinkSent] = useState(false);

  // Where to land after login: an explicit ?next=, else the protected route that
  // bounced us here (ProtectedRoute stashes it in location.state.from, query
  // string included). Validated to an app-internal path so the same value can be
  // forwarded safely through the Google and magic-link round-trips.
  const from = (location.state as { from?: Location } | null)?.from;
  const returnTo =
    safeReturnPath(searchParams.get('next')) ??
    safeReturnPath(from ? `${from.pathname}${from.search}` : null);

  const redirectAfterLogin = useEffectEvent(() => {
    navigate(returnTo ?? '/dashboard', { replace: true });
  });

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      redirectAfterLogin();
    }
  }, [isAuthenticated]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 dark:from-background dark:via-background dark:to-background flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md mb-6 flex justify-center items-center gap-2">
        <img src={dffLogo} alt="DFF" className="h-12" />
        <span className="text-3xl font-bold text-blue-900 dark:text-foreground">
          Flow
        </span>
      </div>

      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white/80 dark:bg-card/80 backdrop-blur-sm py-10 px-4 shadow-xl dark:shadow-none border border-gray-100 dark:border-border sm:rounded-2xl sm:px-10">
          {!linkSent && (
            <>
              <div className="mb-8">
                <h2 className="text-center text-2xl font-semibold text-gray-900 dark:text-foreground">
                  Sign in to your account
                </h2>
                <p className="mt-2 text-center text-sm text-gray-600 dark:text-muted-foreground">
                  Secure access to your fund administration platform
                </p>
              </div>

              <GoogleLogin next={returnTo ?? undefined} />
            </>
          )}

          <MagicLinkForm
            onLinkSent={setLinkSent}
            next={returnTo ?? undefined}
          />

          {!linkSent && import.meta.env.DEV && <DevLoginSection />}
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
